export type CameraParams = {
    azimuth: number;
    elevation: number;
    distance: number;
    source_image_url?: string;
    prompt?: string;
    /** Selected output model for the spawned image node */
    model_id?: string;
    /** UI-only: enable fine-grained slider control (prompt still snaps to nearest preset) */
    continuous?: boolean;
    /**
     * Back-compat: older camera tool stored a Nano Banana quality preset.
     * If present and model_id is missing, we map it to nano-banana-2-{1k|2k|4k}.
     */
    banana_quality?: '1k' | '2k' | '4k';
};

export const DEFAULT_CAMERA_PARAMS: CameraParams = {
    azimuth: 45,
    // Elevation is stored as "仰角" degrees (0–360), where 90° = eye level.
    elevation: 90,
    distance: 1.0,
    // Default (non-continuous) prompt uses the legacy <sks> template.
    prompt: '<sks> front-right quarter view eye-level shot medium shot',
    model_id: 'wavespeed-ai/qwen-image/edit-2511-lora-multiple-angles',
    continuous: false,
};

export function normalizeCameraParams(params: unknown): {
    params: CameraParams;
    needsHydrate: boolean;
} {
    const p = params as Partial<CameraParams>;
    const continuous =
        typeof (p as any)?.continuous === 'boolean' ? Boolean((p as any).continuous) : false;

    const normalizeAngle360 = (deg: number) => {
        const n = ((deg % 360) + 360) % 360;
        return Object.is(n, -0) ? 0 : n;
    };

    // Migration: older camera stored elevation as pitch degrees (-30..60) or normalized pitch (0..360) with 0=eye level.
    const normalizeElevationToSpec = (raw: number): number => {
        const v = Number(raw);
        if (!Number.isFinite(v)) return DEFAULT_CAMERA_PARAMS.elevation;
        const n = normalizeAngle360(v);

        // Continuous mode: treat stored elevation as the new spec directly (0–360, 90=eye level).
        // Do NOT auto-shift ranges like 0–30 or 300–360 (they are valid in the new spec).
        if (continuous) return n;

        // Non-continuous mode expects the 4-step elevation range: 60/90/120/150.
        // If it's already in [60..150], treat as new spec (do NOT migrate).
        if (n >= 60 && n <= 150) return n;

        // Otherwise, attempt to interpret as legacy pitch encoding and convert:
        // pitch (-30..60) => elevation (pitch + 90) in [60..150].
        const pitch = ((n + 180) % 360) - 180; // [-180, 180]
        if (pitch >= -30 && pitch <= 60) return normalizeAngle360(pitch + 90);

        // Fallback to eye level.
        return 90;
    };
    const bananaQuality =
        (p.banana_quality === '1k' || p.banana_quality === '2k' || p.banana_quality === '4k')
            ? p.banana_quality
            : undefined;
    const modelFromBanana =
        bananaQuality === '4k' ? 'nano-banana-2-4k'
            : bananaQuality === '2k' ? 'nano-banana-2-2k'
                : bananaQuality === '1k' ? 'nano-banana-2-1k'
                    : undefined;
    const modelId =
        (typeof (p as any)?.model_id === 'string' && String((p as any).model_id).trim())
            ? String((p as any).model_id).trim()
            : modelFromBanana ?? DEFAULT_CAMERA_PARAMS.model_id;
    return {
        params: {
            azimuth: typeof p.azimuth === 'number' ? p.azimuth : 0,
            elevation: normalizeElevationToSpec(typeof p.elevation === 'number' ? p.elevation : DEFAULT_CAMERA_PARAMS.elevation),
            distance: typeof p.distance === 'number' ? p.distance : 1.0,
            source_image_url: typeof p.source_image_url === 'string' ? p.source_image_url : undefined,
            prompt: typeof p.prompt === 'string' ? p.prompt : undefined,
            model_id: modelId,
            continuous,
            banana_quality: bananaQuality,
        },
        needsHydrate: Boolean((p as any)?.model_id == null && modelFromBanana != null),
    };
}
